import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

// ─── Constants ───────────────────────────────────────────────────────────────

const SIZE = 4;

const TILE_COLORS = {
  0:    { bg: 'transparent',  text: 'transparent', border: 'transparent' },
  2:    { bg: '#eee4da',      text: '#776e65' },
  4:    { bg: '#ede0c8',      text: '#776e65' },
  8:    { bg: '#f2b179',      text: '#f9f6f2' },
  16:   { bg: '#f59563',      text: '#f9f6f2' },
  32:   { bg: '#f67c5f',      text: '#f9f6f2' },
  64:   { bg: '#f65e3b',      text: '#f9f6f2' },
  128:  { bg: '#edcf72',      text: '#f9f6f2' },
  256:  { bg: '#edcc61',      text: '#f9f6f2' },
  512:  { bg: '#edc850',      text: '#f9f6f2' },
  1024: { bg: '#edc53f',      text: '#f9f6f2' },
  2048: { bg: '#edc22e',      text: '#f9f6f2' },
};

function getTileColor(val) {
  return TILE_COLORS[val] || { bg: '#3c3a32', text: '#f9f6f2' };
}

function tileFontSize(val) {
  if (val >= 1024) return '2rem';
  if (val >= 128)  return '2.4rem';
  return '2.8rem';
}

// ─── Pure game logic ─────────────────────────────────────────────────────────

function emptyGrid() {
  return Array(SIZE).fill(null).map(() => Array(SIZE).fill(0));
}

function addRandomTile(grid) {
  const empty = [];
  grid.forEach((row, r) => row.forEach((val, c) => { if (!val) empty.push([r, c]); }));
  if (!empty.length) return grid;
  const [r, c] = empty[Math.floor(Math.random() * empty.length)];
  const g = grid.map(row => [...row]);
  g[r][c] = Math.random() < 0.9 ? 2 : 4;
  return g;
}

function initGrid() {
  return addRandomTile(addRandomTile(emptyGrid()));
}

function slideRow(row) {
  const nums = row.filter(Boolean);
  const out = [];
  let score = 0;
  let i = 0;
  while (i < nums.length) {
    if (i + 1 < nums.length && nums[i] === nums[i + 1]) {
      const merged = nums[i] * 2;
      out.push(merged);
      score += merged;
      i += 2;
    } else {
      out.push(nums[i]);
      i++;
    }
  }
  while (out.length < SIZE) out.push(0);
  return { row: out, score };
}

function transpose(g) {
  return g[0].map((_, c) => g.map(row => row[c]));
}

function flipRows(g) {
  return g.map(row => [...row].reverse());
}

function applyMove(grid, dir) {
  let g = grid.map(r => [...r]);
  let totalScore = 0;

  if (dir === 'up')    g = transpose(g);
  if (dir === 'right') g = flipRows(g);
  if (dir === 'down')  { g = transpose(g); g = flipRows(g); }

  g = g.map(row => {
    const { row: r, score } = slideRow(row);
    totalScore += score;
    return r;
  });

  if (dir === 'up')    g = transpose(g);
  if (dir === 'right') g = flipRows(g);
  if (dir === 'down')  { g = flipRows(g); g = transpose(g); }

  return { grid: g, score: totalScore };
}

function gridsEqual(a, b) {
  return a.every((row, r) => row.every((v, c) => v === b[r][c]));
}

function isGameOver(grid) {
  return ['left', 'right', 'up', 'down'].every(dir => gridsEqual(grid, applyMove(grid, dir).grid));
}

function hasWon(grid) {
  return grid.some(row => row.includes(2048));
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const PageStyles = styled.div`
  padding: 16rem 0 10rem;
  min-height: 80vh;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.4rem;
  color: var(--highlight-blue);
  opacity: 0.7;
  margin-bottom: 3rem;
  transition: opacity 0.2s ease;
  &:hover { opacity: 1; }
  &::before { content: '←'; }
`;

const GameWrapper = styled.div`
  max-width: 520px;
  margin: 0 auto;
  padding: 0 2rem;
  user-select: none;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.6rem;
  flex-wrap: wrap;
  gap: 1rem;

  .game__title {
    font-size: 3.6rem;
    font-family: 'Montserrat Bold';
    color: var(--gray-1);
    line-height: 1;
  }
`;

const ScoreGroup = styled.div`
  display: flex;
  gap: 1rem;
`;

const ScoreBox = styled.div`
  background: var(--deep-dark);
  border: 1px solid var(--gray-2);
  border-radius: 8px;
  padding: 0.6rem 1.4rem;
  text-align: center;
  min-width: 80px;

  .label {
    font-size: 1rem;
    text-transform: uppercase;
    letter-spacing: 0.1rem;
    color: var(--gray-1);
    opacity: 0.45;
  }
  .value {
    font-size: 2rem;
    font-family: 'Montserrat Bold';
    color: var(--highlight-blue);
    line-height: 1.2;
  }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.2rem;
  gap: 1rem;

  .hint {
    font-size: 1.2rem;
    color: var(--gray-1);
    opacity: 0.35;
  }
`;

const NewGameBtn = styled.button`
  font-size: 1.3rem;
  padding: 0.6em 1.8em;
  background: transparent;
  color: var(--highlight-blue);
  border: 2px solid var(--highlight-blue);
  border-radius: 8px;
  cursor: pointer;
  font-family: 'RobotoMono Regular';
  transition: background 0.2s ease, color 0.2s ease;
  white-space: nowrap;
  &:hover { background: var(--highlight-blue); color: var(--dark-bg); }
`;

const BoardWrapper = styled.div`
  position: relative;
  background: var(--deep-dark);
  border-radius: 12px;
  padding: 1rem;
  border: 2px solid var(--gray-2);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
`;

const Cell = styled.div`
  aspect-ratio: 1;
  border-radius: 8px;
  background: var(--gray-2);
  opacity: 0.4;
`;

const TilesLayer = styled.div`
  position: absolute;
  inset: 1rem;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
`;

const Tile = styled.div`
  aspect-ratio: 1;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $val }) => getTileColor($val).bg};
  color: ${({ $val }) => getTileColor($val).text};
  font-size: ${({ $val }) => tileFontSize($val)};
  font-family: 'Montserrat Bold';
  transition: background 0.1s ease;
  box-shadow: ${({ $val }) => $val >= 128 ? '0 2px 8px rgba(0,0,0,0.3)' : 'none'};
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.72);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.2rem;
  z-index: 10;

  h2 {
    font-size: 3rem;
    font-family: 'Montserrat Bold';
    color: ${({ $won }) => $won ? '#edc22e' : 'var(--gray-1)'};
  }
  p {
    font-size: 1.4rem;
    color: var(--gray-1);
    opacity: 0.65;
  }
`;

const OverlayActions = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const OBtn = styled.button`
  font-size: 1.4rem;
  padding: 0.7em 2em;
  border-radius: 8px;
  cursor: pointer;
  font-family: 'RobotoMono Regular';
  border: 2px solid ${({ $primary }) => $primary ? '#edc22e' : 'var(--gray-1)'};
  background: ${({ $primary }) => $primary ? '#edc22e' : 'transparent'};
  color: ${({ $primary }) => $primary ? '#776e65' : 'var(--gray-1)'};
  transition: opacity 0.2s ease;
  &:hover { opacity: 0.8; }
`;

// ─── Component ───────────────────────────────────────────────────────────────

export default function TwentyFortyEight() {
  const [grid, setGrid]           = useState(initGrid);
  const [score, setScore]         = useState(0);
  const [bestScore, setBestScore] = useState(() => Number(localStorage.getItem('2048-best') || 0));
  const [gameOver, setGameOver]   = useState(false);
  const [won, setWon]             = useState(false);
  const [keepPlaying, setKeepPlaying] = useState(false);

  // Touch tracking
  const touchStart = useRef(null);

  const newGame = useCallback(() => {
    setGrid(initGrid());
    setScore(0);
    setGameOver(false);
    setWon(false);
    setKeepPlaying(false);
  }, []);

  const move = useCallback((dir) => {
    setGrid(prev => {
      const { grid: next, score: gained } = applyMove(prev, dir);
      if (gridsEqual(prev, next)) return prev; // nothing moved

      const withTile = addRandomTile(next);

      setScore(s => {
        const newScore = s + gained;
        setBestScore(b => {
          if (newScore > b) {
            localStorage.setItem('2048-best', String(newScore));
            return newScore;
          }
          return b;
        });
        return newScore;
      });

      if (!keepPlaying && hasWon(withTile)) setWon(true);
      if (isGameOver(withTile)) setGameOver(true);

      return withTile;
    });
  }, [keepPlaying]);

  // Keyboard
  useEffect(() => {
    const KEY_MAP = {
      ArrowLeft:  'left',
      ArrowRight: 'right',
      ArrowUp:    'up',
      ArrowDown:  'down',
    };
    const onKey = (e) => {
      const dir = KEY_MAP[e.key];
      if (!dir) return;
      e.preventDefault();
      if (gameOver || (won && !keepPlaying)) return;
      move(dir);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [move, gameOver, won, keepPlaying]);

  // Touch / swipe
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  };

  const onTouchEnd = (e) => {
    if (!touchStart.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;
    touchStart.current = null;

    if (Math.abs(dx) < 20 && Math.abs(dy) < 20) return; // too short
    if (gameOver || (won && !keepPlaying)) return;

    if (Math.abs(dx) > Math.abs(dy)) {
      move(dx > 0 ? 'right' : 'left');
    } else {
      move(dy > 0 ? 'down' : 'up');
    }
  };

  const showOverlay = gameOver || (won && !keepPlaying);

  return (
    <PageStyles>
      <Helmet><title>2048 | Anas Qazi</title></Helmet>
      <div className="container">
        <BackLink to="/games">All Games</BackLink>
      </div>

      <GameWrapper>
        <TopBar>
          <span className="game__title">2048</span>
          <ScoreGroup>
            <ScoreBox>
              <div className="label">Score</div>
              <div className="value">{score}</div>
            </ScoreBox>
            <ScoreBox>
              <div className="label">Best</div>
              <div className="value">{bestScore}</div>
            </ScoreBox>
          </ScoreGroup>
        </TopBar>

        <Controls>
          <span className="hint">Arrow keys · Swipe on mobile</span>
          <NewGameBtn onClick={newGame}>New Game</NewGameBtn>
        </Controls>

        <BoardWrapper
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {/* Background empty cells */}
          <Grid>
            {Array(SIZE * SIZE).fill(null).map((_, i) => <Cell key={i} />)}
          </Grid>

          {/* Tiles layer */}
          <TilesLayer>
            {grid.flat().map((val, i) => (
              <Tile key={i} $val={val}>
                {val !== 0 ? val : ''}
              </Tile>
            ))}
          </TilesLayer>

          {/* Game Over / Win overlay */}
          {showOverlay && (
            <Overlay $won={won}>
              {won ? (
                <>
                  <h2>You reached 2048!</h2>
                  <p>Keep going for a higher score?</p>
                  <OverlayActions>
                    <OBtn $primary onClick={() => { setWon(false); setKeepPlaying(true); }}>
                      Keep Playing
                    </OBtn>
                    <OBtn onClick={newGame}>New Game</OBtn>
                  </OverlayActions>
                </>
              ) : (
                <>
                  <h2>Game Over</h2>
                  <p>Score: {score}</p>
                  <OBtn $primary onClick={newGame}>Try Again</OBtn>
                </>
              )}
            </Overlay>
          )}
        </BoardWrapper>
      </GameWrapper>
    </PageStyles>
  );
}
